import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";

type UseCase = {
  id: string;
  title: string;
  owner: string;
  agent: string;
  impact: number;
  readiness: number;
  risk: "low" | "medium" | "high";
  data_needed: string[];
  model_policy: string;
};

export default function UseCasesPage() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const useCases = useQuery({
    queryKey: ["use-cases", effectiveTenantId],
    queryFn: () => api.get<UseCase[]>("/use-cases"),
  });

  const sorted = [...(useCases.data ?? [])].sort((a, b) => (b.impact + b.readiness) - (a.impact + a.readiness));

  return (
    <section className="flow-page usecase-page">
      <header className="flow-hero">
        <div>
          <p className="hero-eyebrow">KI Use Cases</p>
          <h1>Use Cases nach Impact, Readiness und Risiko priorisieren.</h1>
          <p className="hero-sub">
            Nicht jeder KI-Use-Case darf sofort autonom laufen. Dieses Board macht Datenbedarf, Owner, Agent und Modellpolitik sichtbar.
          </p>
        </div>
        <div className="flow-score">
          <span>Shortlist</span>
          <strong>{sorted.length}</strong>
          <small>priorisiert</small>
        </div>
      </header>

      {useCases.isLoading && <p className="muted">Lade Use Cases...</p>}
      {useCases.error && <p className="error">Use Cases nicht verfügbar.</p>}

      <div className="usecase-grid">
        {sorted.map((item, index) => (
          <article key={item.id} className="usecase-card">
            <div className="card-kicker">#{index + 1} · {item.owner}</div>
            <h2>{item.title}</h2>
            <div className="score-row">
              <span>Impact <strong>{item.impact}</strong></span>
              <span>Readiness <strong>{item.readiness}</strong></span>
              <span className={`risk ${item.risk}`} title={`Risiko: ${item.risk}`}>
                Risiko {item.risk === "low" ? "gering" : item.risk === "medium" ? "mittel" : "hoch"}
              </span>
            </div>
            <p className="muted small">Agent: <strong>{item.agent}</strong> · Policy: <code>{item.model_policy}</code></p>
            <ul className="inline-list">
              {item.data_needed.map((need) => <li key={need}>{need}</li>)}
            </ul>
          </article>
        ))}
      </div>

      <section className="flow-panel">
        <h2>Naechster Schritt</h2>
        <p className="muted">Top-Use-Cases werden erst nach Quality Gate und HITL-Owner-Freigabe produktiv aktiviert.</p>
        <div className="flow-actions">
          <Link to="/app/sources" className="btn primary">Datenquellen vorbereiten</Link>
          <Link to="/app/advisor?mode=use_cases&persona=CEO" className="btn ghost">CEO-Berater fragen</Link>
        </div>
      </section>
    </section>
  );
}
